import React, { useEffect, useRef, useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COUNTRIES = [
  ['CH', 'Zwitserland'], ['IT', 'Italië'], ['DE', 'Duitsland'], ['AT', 'Oostenrijk'], ['FR', 'Frankrijk'],
  ['LI', 'Liechtenstein'], ['BE', 'België'], ['NL', 'Nederland'],
];
const COUNTRY_ALIASES = { switzerland: 'CH', zwitserland: 'CH', italy: 'IT', germany: 'DE', austria: 'AT', france: 'FR', liechtenstein: 'LI', belgium: 'BE', netherlands: 'NL' };
const toCode = (c) => {
  const v = (c || '').trim();
  if (COUNTRIES.some(([code]) => code === v.toUpperCase())) return v.toUpperCase();
  return COUNTRY_ALIASES[v.toLowerCase()] || 'CH';
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-zinc-600">{label}</span>
    {children}
  </label>
);
const inputCls = 'mt-1 w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500';

const AddressSearch = ({ onPick }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 3) { setResults([]); return undefined; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/address-search`, {
          params: { q }, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setResults(res.data.results);
        setOpen(true);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [q]);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => results.length && setOpen(true)}
               placeholder="Zoek adres of bedrijf (Zwitserland / Italië)…"
               className="w-full border-2 border-purple-300 rounded-lg pl-9 pr-9 py-2.5 text-sm focus:outline-none focus:border-purple-500"
               data-testid="pickup-address-search" />
        {loading && <Loader2 className="w-4 h-4 text-purple-500 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />}
      </div>
      {open && q.trim().length >= 3 && !loading && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-zinc-200 rounded-lg shadow-lg max-h-64 overflow-auto" data-testid="pickup-address-results">
          {results.length === 0 && <div className="px-3 py-2 text-sm text-zinc-500">Geen adres gevonden — vul het hieronder handmatig in</div>}
          {results.map((r, i) => (
            <button key={i} type="button"
                    onClick={() => { onPick(r); setOpen(false); setQ(''); }}
                    className="w-full text-left px-3 py-2 hover:bg-purple-50 border-b border-zinc-100 last:border-0"
                    data-testid={`pickup-address-result-${i}`}>
              {r.name && <div className="text-sm font-semibold text-zinc-900">{r.name}</div>}
              <div className="text-sm text-zinc-700">{[r.street, [r.postal_code, r.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</div>
              <div className="text-[10px] uppercase tracking-wide text-zinc-400">{r.country === 'CH' ? 'Zwitserland' : 'Italië'}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// target: { orderIds, userId, edit: {company, street, postal_code, city, country, phone}, title }
// Kies een leverancier die al in het systeem staat (dealer-account of eerder bewaard adres).
const KnownSupplierPicker = ({ onPick }) => {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      axios.get(`${API}/admin/chauffeur-known-suppliers`, { params: { q }, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
        .then((r) => setRows(r.data.suppliers)).catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);
  return (
    <div className="relative" data-testid="known-supplier-picker">
      <label className="text-xs font-semibold text-zinc-700">Bestaande leverancier kiezen</label>
      <input value={q} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
             placeholder="Typ naam of plaats, bv. Moto Battig…" className="w-full mt-1 border border-purple-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
             data-testid="known-supplier-search" />
      {open && rows.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {rows.map((r, i) => (
            <button key={`${r.company}-${i}`} type="button" data-testid={`known-supplier-option-${i}`}
                    onClick={() => { onPick(r); setQ(r.company); setOpen(false); }}
                    className="w-full text-left px-3 py-2 hover:bg-purple-50 border-b border-zinc-100 last:border-0">
              <div className="text-sm font-semibold text-zinc-900">{r.company}</div>
              <div className="text-[11px] text-zinc-500">{[r.street, `${r.postal_code} ${r.city}`.trim(), r.country].filter(Boolean).join(', ')}</div>
            </button>
          ))}
        </div>
      )}
      <div className="text-[11px] text-zinc-400 mt-1">Of vul hieronder zelf een (nieuw) adres in.</div>
    </div>
  );
};

export const PickupAddressDialog = ({ target, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [saveToSupplier, setSaveToSupplier] = useState(true);
  const [busy, setBusy] = useState(false);
  const [linked, setLinked] = useState(false);

  useEffect(() => {
    if (!target) return;
    setLinked(false);
    const e = target.edit || {};
    setForm({ company: e.company || '', street: e.street || '', postal_code: e.postal_code || '', city: e.city || '', country: toCode(e.country), phone: e.phone || '' });
    setSaveToSupplier(true);
  }, [target]);

  const set = (k) => (ev) => { if (k === 'company') setLinked(false); setForm((f) => ({
    ...f, [k]: ev.target.value, ...(['street', 'postal_code', 'city', 'country'].includes(k) ? { lat: null, lon: null } : {}),
  })); };
  const pickKnown = (r) => {
    setForm({ company: r.company, street: r.street, postal_code: r.postal_code, city: r.city, country: toCode(r.country), phone: r.phone, lat: null, lon: null });
    setLinked(true);
  };
  const pick = (r) => setForm((f) => ({
    ...f, company: r.name || f.company, street: r.street, postal_code: r.postal_code, city: r.city,
    country: r.country, lat: r.lat, lon: r.lon,
  }));

  const originalName = (target?.edit?.company || '').trim().toLowerCase();
  const otherSupplier = !!originalName && (form.company || '').trim().toLowerCase() !== originalName;
  const toSupplier = linked || (saveToSupplier && !otherSupplier);

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/chauffeur-pickup-address`, {
        ...form, order_ids: target.orderIds, user_id: linked ? null : (target.userId || null), save_to_supplier: toSupplier,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('Ophaaladres opgeslagen');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="pickup-address-dialog">
        <DialogHeader>
          <DialogTitle>{target?.title || 'Ophaaladres'}</DialogTitle>
          <DialogDescription>Waar haalt de chauffeur de motor op?</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <KnownSupplierPicker onPick={pickKnown} />
          <AddressSearch onPick={(r) => { setLinked(false); pick(r); }} />
          {form.lat != null && (
            <div className="text-[11px] text-emerald-700" data-testid="pickup-location-confirmed">✓ Locatie gevonden — wordt direct gebruikt voor de route</div>
          )}
          <Field label="Bedrijfsnaam">
            <input value={form.company || ''} onChange={set('company')} className={inputCls} data-testid="pickup-company" />
          </Field>
          <Field label="Straat + huisnummer">
            <input value={form.street || ''} onChange={set('street')} placeholder="Glarnerstrasse 61a" className={inputCls} data-testid="pickup-street" />
          </Field>
          <div className="grid grid-cols-[110px_1fr] gap-2">
            <Field label="Postcode">
              <input value={form.postal_code || ''} onChange={set('postal_code')} placeholder="8854" className={inputCls} data-testid="pickup-postal" />
            </Field>
            <Field label="Plaats">
              <input value={form.city || ''} onChange={set('city')} placeholder="Siebnen" className={inputCls} data-testid="pickup-city" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Land">
              <select value={form.country || 'CH'} onChange={set('country')} className={inputCls} data-testid="pickup-country">
                {COUNTRIES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
              </select>
            </Field>
            <Field label="Telefoon">
              <input value={form.phone || ''} onChange={set('phone')} placeholder="+41 …" className={inputCls} data-testid="pickup-phone" />
            </Field>
          </div>
          {linked ? (
            <div className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-lg p-2.5" data-testid="pickup-linked-note">
              <b>Gekoppeld aan {form.company}</b>
              <span className="block text-xs">Deze motor(en) worden bij deze leverancier opgehaald.</span>
            </div>
          ) : otherSupplier ? (
            <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-2.5" data-testid="pickup-other-supplier-note">
              <b>Andere leverancier</b>
              <span className="block text-xs">Wordt alleen voor deze pakbon(nen) opgeslagen. "{target?.edit?.company}" blijft ongewijzigd.</span>
            </div>
          ) : (
            <label className="flex items-start gap-2 text-sm text-zinc-700 bg-zinc-50 rounded-lg p-2.5 cursor-pointer">
              <input type="checkbox" checked={saveToSupplier} onChange={(e) => setSaveToSupplier(e.target.checked)}
                     className="mt-0.5 w-4 h-4 accent-purple-600" data-testid="pickup-save-to-supplier" />
              <span>
                <b>Opslaan bij de leverancier</b>
                <span className="block text-xs text-zinc-500">
                  {saveToSupplier ? 'Geldt ook voor alle volgende pakbonnen van deze leverancier.' : 'Alleen voor deze pakbon(nen).'}
                </span>
              </span>
            </label>
          )}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-zinc-300" data-testid="pickup-cancel">Annuleren</button>
          <button onClick={save} disabled={busy || !form.company || !(form.street || form.city)}
                  className="px-4 py-2 text-sm rounded-lg bg-purple-600 text-white font-semibold disabled:opacity-40" data-testid="pickup-save">
            {busy ? 'Opslaan…' : 'Opslaan'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
